import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Circle, Clock, Smartphone, MessageCircle, ShieldCheck, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export default function AcompanharOS() {
  const [params] = useSearchParams();
  const os = params.get("os");
  const c = params.get("c");
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error

  useEffect(() => {
    if (!os || !c) { setState("error"); return; }
    fetch(`${BACKEND}/api/public/os-status?os=${encodeURIComponent(os)}&c=${encodeURIComponent(c)}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => { setData(d); setState("ok"); })
      .catch(() => setState("error"));
  }, [os, c]);

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white flex flex-col items-center px-4 py-10" data-testid="acompanhar-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <span className="h-9 w-9 rounded-xl bg-gold/15 border border-gold/30 flex items-center justify-center">
            <ShieldCheck size={18} className="text-gold-bright" />
          </span>
          <span className="font-display font-extrabold text-xl tracking-tight">{data?.company?.name || "Alpha Prime"}</span>
        </div>

        {state === "loading" && (
          <div className="flex flex-col items-center gap-3 text-zinc-400 mt-16" data-testid="acompanhar-loading">
            <Loader2 className="animate-spin text-gold-bright" size={28} />
            <p>Carregando sua ordem de serviço…</p>
          </div>
        )}

        {state === "error" && (
          <div className="alpha-card p-8 text-center" data-testid="acompanhar-error">
            <p className="text-lg font-semibold text-white">Ordem de serviço não encontrada</p>
            <p className="text-sm text-zinc-400 mt-2">Confira o QR Code ou o link do seu comprovante de entrada. Em caso de dúvida, fale com a loja.</p>
          </div>
        )}

        {state === "ok" && data && (
          <div className="space-y-6" data-testid="acompanhar-content">
            <div className="alpha-card p-5">
              <p className="text-sm text-zinc-400">Olá {data.customerFirstName || "cliente"}! Acompanhe seu reparo:</p>
              <div className="flex items-center gap-3 mt-3">
                <span className="h-11 w-11 rounded-xl bg-gold/15 border border-gold/30 flex items-center justify-center shrink-0">
                  <Smartphone size={20} className="text-gold-bright" />
                </span>
                <div>
                  <div className="font-mono-kpi font-extrabold alpha-gold-text text-xl" data-testid="acompanhar-number">{data.number}</div>
                  <div className="text-sm text-zinc-300">{data.device || "Aparelho"}</div>
                </div>
              </div>
              <div className="mt-4 inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-full font-medium"
                   style={{ background: `${data.status?.color || "#AA820A"}22`, color: data.status?.color || "#AA820A" }}
                   data-testid="acompanhar-status">
                <Clock size={14} /> {data.status?.label || data.status?.key}
              </div>
              <p className="text-xs text-zinc-500 mt-3">Aberta em {fmt(data.createdAt)} · atualizada em {fmt(data.updatedAt)}</p>
            </div>

            <div className="alpha-card p-5">
              <h3 className="font-display font-semibold text-white mb-4">Status do reparo</h3>
              <ol className="space-y-3">
                {(() => {
                  const OUTCOME_KEYS = ["sem_conserto", "reparado_aguardando"];
                  const curKey = data.status?.key;
                  const curOrder = data.status?.order ?? 0;
                  const visited = new Set((data.timeline || []).map((t) => t.to).filter(Boolean));
                  if (curKey) visited.add(curKey);
                  const takenOutcome = OUTCOME_KEYS.includes(curKey) ? curKey : OUTCOME_KEYS.find((k) => visited.has(k));
                  const steps = (data.all_statuses || []).filter((s) =>
                    !OUTCOME_KEYS.includes(s.key) || !takenOutcome || s.key === takenOutcome
                  );
                  return steps.map((s) => {
                    const active = s.key === curKey;
                    const done = !active && ((s.order ?? 0) < curOrder || data.status?.final);
                    return (
                      <li key={s.key} className="flex items-center gap-3" data-testid={`step-${s.key}`}>
                        {active ? <Clock size={18} className="text-gold-bright shrink-0" />
                          : done ? <CheckCircle2 size={18} className="text-green-400 shrink-0" />
                          : <Circle size={18} className="text-zinc-600 shrink-0" />}
                        <span className={active ? "text-white font-semibold" : done ? "text-zinc-300" : "text-zinc-500"}>{s.label}</span>
                      </li>
                    );
                  });
                })()}
              </ol>
            </div>

            {data.timeline?.length > 0 && (
              <div className="alpha-card p-5">
                <h3 className="font-display font-semibold text-white mb-4">Histórico</h3>
                <ul className="space-y-3">
                  {data.timeline.map((t, i) => (
                    <li key={i} className="flex gap-3 text-sm" data-testid={`history-${i}`}>
                      <span className="text-gold-bright mt-0.5">•</span>
                      <div>
                        <div className="text-zinc-200">{t.note}</div>
                        <div className="text-xs text-zinc-500">{fmt(t.at)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.company?.whatsapp && (
              <a href={`https://wa.me/${(data.company.whatsapp || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                 data-testid="acompanhar-whatsapp"
                 className="flex items-center justify-center gap-2 h-11 rounded-xl bg-green-600 hover:bg-green-500 text-white font-semibold transition-colors">
                <MessageCircle size={18} /> Falar com a {data.company?.name || "loja"}
              </a>
            )}
            <p className="text-center text-xs text-zinc-600 pb-6">Página de acompanhamento · {data.company?.name || "Alpha Prime"}</p>
          </div>
        )}
      </div>
    </div>
  );
}
